import { Component, forwardRef, Input, OnInit } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: "app-time-picker",
  templateUrl: "./time-picker.component.html",
  styleUrls: ["./time-picker.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TimePickerComponent implements OnInit, ControlValueAccessor {
  @Input() label = "";
  @Input() placeholder = "";
  @Input() disabled = false;

  Math = Math;
  value = "";
  hour = 0;
  minute = 0;
  hourOffset = 0;
  minuteOffset = 0;

  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  minutes: number[] = Array.from({ length: 60 }, (_, i) => i);

  private onChange: (value: string) => void = () => {};
  private onTouched = () => {};

  ngOnInit(): void {
    if (!this.value) {
      const now = new Date();
      this.hour = now.getHours();
      this.minute = now.getMinutes();
      this.updateOffsets();
      this.updateValue();
    }
  }

  selectTime(value: number, type: "hour" | "minute"): void {
    if (type === "hour") {
      this.hour = value;
    } else {
      this.minute = value;
    }
    this.updateOffsets();
    this.updateValue();
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    const match = inputValue.match(timeRegex);

    if (match) {
      this.hour = parseInt(match[1], 10);
      this.minute = parseInt(match[2], 10);
      this.updateOffsets();
      this.value = inputValue;
      this.onChange(this.value);
    } else {
      this.value = inputValue;
      this.onChange(this.value);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  onWheel(event: WheelEvent, type: "hour" | "minute"): void {
    event.preventDefault();
    const max = type === "hour" ? 23 : 59;
    const current = type === "hour" ? this.hour : this.minute;
    const newValue =
      event.deltaY < 0 ? Math.min(max, current + 1) : Math.max(0, current - 1);

    if (type === "hour") {
      this.hour = newValue;
    } else {
      this.minute = newValue;
    }
    this.updateOffsets();
    this.updateValue();
  }

  private updateValue(): void {
    this.value = `${String(this.hour).padStart(2, "0")}:${String(this.minute).padStart(2, "0")}`;
    this.onChange(this.value);
    this.onTouched();
  }

  private updateOffsets(): void {
    this.hourOffset = 33 - this.hour * 33;
    this.minuteOffset = 33 - this.minute * 33;
  }

  formatNumber(num: number): string {
    return String(num).padStart(2, "0");
  }

  writeValue(value: string): void {
    this.value = value || "";
    if (this.value) {
      const [hours, minutes] = this.value.split(":").map(Number);
      this.hour = hours || 0;
      this.minute = minutes || 0;
      this.updateOffsets();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
